/* eslint-disable no-param-reassign */
// External
import {
  getBlob,
  uploadString,
  deleteObject,
  getDownloadURL,
  listAll,
  ref as storageRef,
} from 'firebase/storage'
import {
  doc,
  where,
  query,
  getDoc,
  setDoc,
  getDocs,
  updateDoc,
  collection,
  onSnapshot,
  getCountFromServer,
} from 'firebase/firestore'
import { callFunction } from '@/libs/firebase'
import { getUnix } from '@/libs/date-format'
import html2canvas from 'html2canvas'
import { RevisionEventType } from '@core/revisions/revisions'

// Local
import store from '@/store'
import i18n from '@/libs/i18n'
import { allowed, FEATURES } from '@/auth'
import {
  constructQuery,
  updateIframeElement,
  retrieveThemeExternalResources as retrieveExternalResources,
} from '@core/utils/utils'

import { getTenantContextInstance as tenantCtx } from '@/plugins/tenant'
// In-flight `fetchCurrent` promises, keyed by Theme ID.
//
// The Theme edit page mounts all of its tabs (Assets Editor, UI Components Editor,
// External Resources, Settings) at once, and several of them independently call
// `fetchCurrent` for the same Theme ID on mount. Without de-duplication this fires
// the same 3-read sequence (Theme doc + files + external resources) multiple times
// in parallel, which is what causes the noticeable load delay when opening/switching
// Theme edit tabs. Callers made while a request is already in-flight simply await the
// same Promise instead of starting a new one.
const fetchCurrentInFlight = new Map()

const THUMBNAIL_DEVICES = ['mobile', 'tablet', 'desktop']

/**
 * Lists existing thumbnail filenames under `{themeId}/{path}/images/` without
 * probing missing objects (avoids a Storage 404 storm for every html file × device).
 *
 * @param {Object} bucket - Firebase Storage bucket ref root
 * @param {string} themeId
 * @param {string} path - file path folder (e.g. components/hero)
 * @returns {Promise<Set<string>>}
 */
const listThumbnailNames = async (bucket, themeId, path) => {
  try {
    const listed = await listAll(storageRef(bucket, `${themeId}/${path}/images`))
    return new Set(listed.items.map(item => item.name))
  } catch {
    return new Set()
  }
}

/**
 * Resolves signed URLs for device thumbnails that actually exist in Storage.
 * Missing devices fall back to the pages thumbnail placeholder (no 404 probe).
 *
 * @param {Object} bucket
 * @param {string} themeId
 * @param {{ path: string, name: string, type: string }} file
 * @param {Set<string>} [existingNames] - optional pre-listed filenames in images/
 * @returns {Promise<Record<string, string>>}
 */
const resolveDeviceThumbnailUrls = async (bucket, themeId, file, existingNames = null) => {
  const placeholder = store.getters['pages/getThumbnailPlaceholder']
  const names = existingNames || await listThumbnailNames(bucket, themeId, file.path)
  const urls = {}

  await Promise.all(
    THUMBNAIL_DEVICES.map(async device => {
      const fileName = `${file.name}.${file.type}.${device}.png`
      if (!names.has(fileName)) {
        urls[device] = placeholder
        return
      }

      try {
        urls[device] = await getDownloadURL(
          storageRef(bucket, `${themeId}/${file.path}/images/${fileName}`),
        )
      } catch {
        urls[device] = placeholder
      }
    }),
  )

  return urls
}

export default {
  namespaced: true,
  state: {
    count: 0,
    current: {
      data: {},
    },
    currentFiles: {},
    activeTheme: null,
    newThemeFolder: {
      form: {},
      parentNode: {
        children: [],
      },
    },
    tenantThemesList: [],
    sharedThemesList: [],
    currentComponents: [],
    externalResources: [],
    uiComponentThumbnails: [],
    thumbnailCanvasElement: null,
    lastActiveThemeEditTab: localStorage.getItem('novti-last-active-theme-edit-tab') || 0,
  },
  getters: {
    getCurrent: state => state.current,
    getCount: state => state.count,
    getActiveTheme: state => state.activeTheme,
    getCurrentFiles: state => state.currentFiles,
    getNewThemeFolder: state => state.newThemeFolder,
    getTenantThemes: state => state.tenantThemesList,
    getSharedThemes: state => state.sharedThemesList,
    getCurrentComponents: state => state.currentComponents,
    getUIComponentThumbnails: state => state.uiComponentThumbnails,
    getThumbnailCanvasElement: state => state.thumbnailCanvasElement,
    getLastActiveThemeEditTab: state => state.lastActiveThemeEditTab,
    getExternalResources: state => type => state.externalResources.filter(item => item.type === type),
    getActiveExternalResources: state => type => state.activeTheme?.externalResources.filter(item => item.type === type),
  },
  mutations: {
    SET_CURRENT_FILES(state, payload) {
      state.currentFiles = payload
    },
    SET_ACTIVE_THEME(state, payload) {
      state.activeTheme = payload ?? {}
    },
    SET_CURRENT_COMPONENTS(state, payload) {
      state.currentComponents = payload ?? []
    },
    SET_THUMBNAIL_CANVAS_ELEMENT(state, payload) {
      state.thumbnailCanvasElement = payload
    },
    SET_CURRENT(state, payload) {
      state.current = {
        snapshot: payload,
        data: { ...payload.data(), id: payload.id },
      }
    },
    SET_CURRENT_EXTERNAL_RESOURCES(state, payload) {
      state.externalResources = payload
    },
    UPDATE_TENANT_THEMES_LIST(state, payload) {
      state.tenantThemesList = payload
    },
    UPDATE_SHARED_THEMES_LIST(state, payload) {
      state.sharedThemesList = payload
    },
    SET_UI_COMPONENT_THUMBNAILS(state, payload) {
      state.uiComponentThumbnails = payload
    },
    DESTROY_CURRENT(state) {
      state.current = {}
    },
    SET_LAST_ACTIVE_THEME_EDIT_TAB(state, payload) {
      localStorage.setItem('novti-last-active-theme-edit-tab', payload)

      state.lastActiveThemeEditTab = payload
    },
    SET_NEW_THEME_FOLDER(state, payload) {
      state.newThemeFolder = payload
    },
    SET_COUNT(state, payload) {
      state.count = payload
    },
  },
  actions: {
    /**
     * Creates a document by the given payload.
     *
     * @param {Object} obj
     * @param {Object} payload
     *
     * @returns {Promise}
     */
    create({}, payload) {
      return new Promise(async (resolve, reject) => {
        if (!allowed(FEATURES.THEMES_QUOTA)) {
          reject()

          store.dispatch('notify', {
            body: i18n.t('You have reached your {title} limit', { title: i18n.t('Themes') }),
            variant: 'danger',
          })

          return
        }

        const { themes } = tenantCtx()
        const { themesStorage } = tenantCtx()

        const docRef = await doc(themes)

        // This is required if we want to filter on those fields, as per Firestore query limitations
        // eslint-disable-next-line no-param-reassign
        payload.id = docRef.id
        payload.active = false
        payload.deletedAt = null
        payload.creating = true
        payload.compiling = false
        payload.createdAt = getUnix()
        payload.updatedAt = getUnix()
        payload.storage = `${themesStorage.fullPath}/${docRef.id}`

        // Provenance: capture the base theme version and origin at creation time
        // These are used later for update-available detection and merge operations.
        if (!payload.origin) {
          payload.origin = payload.baseTheme ? 'install' : 'blank'
        }
        // baseThemeVersion / baseThemeId are set client-side when a shared theme is selected
        // and confirmed by handleNewBaseTheme (CF) which stamps the final values.

        setDoc(docRef, payload)
          .then(() => {
            store.dispatch('revisions/create', {
              event: RevisionEventType.CREATE_THEMES,
              id: docRef.id,
              newValue: payload,
              previousValue: {},
            })

            store.dispatch('notify', {
              title: i18n.t('Great!'),
              body: i18n.t('A new {title} has been created 🚀', {
                title: i18n.t('Theme'),
              }),
            })

            resolve(docRef)
          })
          .catch(error => {
            console.debug(error)

            store.dispatch('notify', {
              body: i18n.t('Something went wrong creating the Theme'),
              variant: 'danger',
            })

            reject(error)
          })
      })
    },

    /**
     * Fetches the shared Themes.
     *
     * @param {Object} obj
     * @param {Object} obj.commit
     * @param {Object} obj.dispatch
     *
     * @returns {Promise}
     */
    fetchSharedThemes({ commit, dispatch }) {
      return new Promise(async (resolve, reject) => {
        try {
          const { sharedThemes } = tenantCtx()

          // Fetch all non-deleted shared themes regardless of visibility.
          // Visibility is a catalog UI concept only — 'private' themes are hidden
          // from the self-service catalog in the UI (useNewTheme.publicSharedThemes)
          // but their metadata must be available so update-available checks work
          // for tenants who already have a private theme installed.
          const snap = await getDocs(query(sharedThemes, where('deletedAt', '==', null)))
          const allDocs = snap.docs

          const { sharedThemesStorage } = tenantCtx()

          const themes = await Promise.all(
            allDocs.map(async docRef => {
              const data = docRef.data()

              // Resolve previewImagePath → signed Firebase Storage URL so the
              // admin UI can display it without a CDN (bucket has public access
              // prevention enforced, so raw CDN URLs won't work).
              let previewImageUrl = null
              if (data.previewImagePath) {
                try {
                  const imgRef = storageRef(sharedThemesStorage, `${docRef.id}/${data.previewImagePath}`)
                  previewImageUrl = await getDownloadURL(imgRef)
                } catch {
                  // File not yet uploaded or permission error — fall through to
                  // the legacy _themeThumbnails path.
                }
              }

              // Same resolution for the optional distinct mobile-viewport preview
              // (e.g. "assets/thumbnail-mobile.png"), shown alongside the desktop
              // preview in the admin UI.
              let previewImageMobileUrl = null
              if (data.previewImageMobilePath) {
                try {
                  const imgRef = storageRef(sharedThemesStorage, `${docRef.id}/${data.previewImageMobilePath}`)
                  previewImageMobileUrl = await getDownloadURL(imgRef)
                } catch {
                  // File not yet uploaded or permission error.
                }
              }

              return {
                id: docRef.id,
                _themeThumbnails: await dispatch('fetchThemeThumbnails', { id: docRef.id, target: 'shared' }),
                ...data,
                // Resolved signed Firebase Storage URL (or null if unavailable).
                // Takes precedence over any stale CDN URL that may have been
                // written to Firestore by an older version of the deploy script.
                previewImageUrl,
                previewImageMobileUrl,
              }
            }),
          )

          commit('UPDATE_SHARED_THEMES_LIST', themes)
          resolve(themes)
        } catch (error) {
          store.dispatch('notify', {
            variant: 'danger',
            body: i18n.t('Something went wrong retrieving the {title}', { title: i18n.t('Novti Themes') }),
          })

          reject(error)
        }
      })
    },

    /**
     * Fetches the release notes history for a shared theme.
     *
     * @param {Object} _ctx
     * @param {string} sharedThemeId - The shared (novti-themes) Firestore doc ID
     *
     * @returns {Promise<Array>} Sorted array of release docs (oldest first)
     */
    fetchThemeReleases(_ctx, sharedThemeId) {
      return new Promise(async (resolve, reject) => {
        try {
          const { sharedThemes } = tenantCtx()
          const releasesCollection = collection(sharedThemes, sharedThemeId, 'releases')
          const releasesSnap = await getDocs(releasesCollection)

          const releases = releasesSnap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .sort((a, b) => (a.releasedAt ?? 0) - (b.releasedAt ?? 0))

          resolve(releases)
        } catch (error) {
          reject(error)
        }
      })
    },

    /**
     * Installs a private shared theme by its Firestore document ID.
     * Requires the `install:private_themes` permission. Passes
     * `plenipotentInstall: true` so handleNewBaseTheme skips the private-theme
     * guard; firestore.rules only accepts that flag from permitted users.
     *
     * @param {Object} _ctx
     * @param {{ sharedThemeDocId: string, name: string, description?: string }} payload
     * @returns {Promise<DocumentReference>}
     */
    installSharedThemeById(_ctx, payload) {
      const { sharedThemes } = tenantCtx()
      const sharedRef = doc(sharedThemes, payload.sharedThemeDocId)

      return getDoc(sharedRef).then(sharedSnap => {
        if (!sharedSnap.exists() || sharedSnap.get('deletedAt') != null) {
          return Promise.reject(new Error('Shared theme not found'))
        }

        const shared = sharedSnap.data()

        return store.dispatch('themes/create', {
          name: payload.name,
          description: payload.description || '',
          baseTheme: sharedSnap.id,
          baseThemeVersion: shared.version ?? null,
          baseThemeId: shared.themeId ?? null,
          plenipotentInstall: true,
          origin: 'install',
        })
      })
    },

    /**
     * Triggers the Shopify-style theme update via the updateThemeFromBase Cloud Function.
     * Creates an inactive draft with new shared content + preserved tenant customisations.
     *
     * @param {Object} _ctx
     * @param {string} themeId - The tenant theme doc ID to update
     *
     * @returns {Promise<{ draftId: string, existing: boolean }>}
     */
    updateThemeFromBase(_ctx, themeId) {
      return new Promise(async (resolve, reject) => {
        try {
          const result = await callFunction('updateThemeFromBase', { themeId })
          resolve(result)
        } catch (error) {
          store.dispatch('notify', {
            variant: 'danger',
            body: i18n.t('Something went wrong creating the theme update draft'),
          })
          reject(error)
        }
      })
    },

    /**
     * Lists merge statuses (or one file's comparison panes) for an update draft.
     *
     * @param {Object} _ctx
     * @param {{ themeId: string, pathKey?: string }} payload
     */
    getThemeUpdateComparison(_ctx, payload) {
      return callFunction('getThemeUpdateComparison', payload)
    },

    /**
     * Resolves one conflicting file on an update draft.
     *
     * @param {Object} _ctx
     * @param {{ themeId: string, pathKey: string, resolution: string, content?: string }} payload
     */
    resolveThemeUpdateConflict(_ctx, payload) {
      return callFunction('resolveThemeUpdateConflict', payload)
    },

    /**
     * Re-merge live source customizations into an update draft.
     *
     * @param {Object} _ctx
     * @param {{ themeId: string }} payload
     */
    resyncThemeUpdateDraft(_ctx, payload) {
      return callFunction('resyncThemeUpdateDraft', payload)
    },

    /**
     * Recompiles a Theme's public bundles (styles.bundle.css / app.bundle.js).
     *
     * @param {Object} _ctx
     * @param {string} id - Theme ID
     */
    triggerThemeCompiler(_ctx, id) {
      return callFunction('triggerThemeCompiler', { id })
    },

    /**
     * Resolves authenticated Storage download URLs for a Theme's compiled bundles.
     * Used when the public CDN does not serve the themes bucket.
     *
     * @param {Object} _ctx
     * @param {string} themeId
     *
     * @returns {Promise<{ css: string|null, js: string|null }>}
     */
    async getThemeBundleDownloadUrls(_ctx, themeId) {
      const { themesStorage } = tenantCtx()

      const resolve = async file => {
        try {
          return await getDownloadURL(storageRef(themesStorage, `${themeId}/public/${file}`))
        } catch (error) {
          if (error?.code !== 'storage/object-not-found') {console.debug(error)}

          return null
        }
      }

      const [css, js] = await Promise.all([
        resolve('styles.bundle.css'),
        resolve('app.bundle.js'),
      ])

      return { css, js }
    },

    /**
     * Loads what is needed to render a page with a Theme, without touching
     * `current` / `activeTheme` state (unlike fetchCurrent / fetchActiveTheme).
     *
     * @param {Object} _ctx
     * @param {string|null} themeId - Theme ID, or null for the tenant's active Theme
     *
     * @returns {Promise<{ id: string, theme: Object, externalResources: Array }|null>}
     */
    async fetchThemePreviewContext(_ctx, themeId = null) {
      const { themes } = tenantCtx()

      let themeSnap

      if (themeId) {
        themeSnap = await getDoc(doc(themes, themeId))
        if (!themeSnap.exists() || themeSnap.get('deletedAt') != null) {
          throw new Error('404')
        }
      } else {
        const activeSnap = await getDocs(query(themes, where('active', '==', true)))

        themeSnap = activeSnap.docs.find(d => d.get('deletedAt') == null)
        if (!themeSnap) return null
      }

      const resourcesSnap = await getDocs(collection(themes, themeSnap.id, 'externalResources'))
      const externalResources = resourcesSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(r => r.deletedAt == null)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))

      return {
        id: themeSnap.id,
        theme: { ...themeSnap.data(), id: themeSnap.id },
        externalResources,
      }
    },

    /**
     * Fetches the tenant's Themes.
     *
     * @param {Object} obj
     * @param {Object} obj.commit
     * @param {Object} obj.dispatch
     *
     * @returns {Promise}
     */
    fetchTenantThemes({ commit, dispatch }) {
      return new Promise((resolve, reject) => {
        const { themes } = tenantCtx()

        const q = constructQuery(themes, 'updatedAt', {
          where: [where('deletedAt', '==', null)],
        })

        getDocs(q)
          .then(async querySnapshot => {
            const tenantThemes = await Promise.all(
              querySnapshot.docs.map(async docRef => ({
                id: docRef.id,
                _themeThumbnails: await dispatch('fetchThemeThumbnails', { id: docRef.id }),
                ...docRef.data(),
              })),
            )

            commit('UPDATE_TENANT_THEMES_LIST', tenantThemes)
            resolve(tenantThemes)
          })
          .catch(error => {
            store.dispatch('notify', {
              variant: 'danger',
              body: i18n.t('Something went wrong retrieving the {title}', { title: i18n.t('tenant Themes') }),
            })

            reject(error)
          })
      })
    },

    /**
     * Refreshes only the build state (`compiling`) of the already loaded tenant
     * Themes. Much cheaper than `fetchTenantThemes`, which also lists thumbnails,
     * so it is suitable for polling while a theme is being built.
     *
     * @param {Object} obj
     * @param {Object} obj.state
     * @param {Object} obj.commit
     *
     * @returns {Promise<Array>} The updated Themes list
     */
    async refreshTenantThemeBuildStates({ state, commit }) {
      const { themes } = tenantCtx()

      const snapshot = await getDocs(constructQuery(themes, 'updatedAt', {
        where: [where('deletedAt', '==', null)],
      }))

      const stateById = new Map(snapshot.docs.map(d => [d.id, d.data()]))

      const refreshed = state.tenantThemesList
        .filter(theme => stateById.has(theme.id))
        .map(theme => ({
          ...theme,
          compiling: stateById.get(theme.id).compiling ?? false,
          compilingSince: stateById.get(theme.id).compilingSince ?? null,
        }))

      commit('UPDATE_TENANT_THEMES_LIST', refreshed)

      return refreshed
    },

    /**
     * Fetches the active Theme.
     *
     * @param {Object} obj
     * @param {Object} obj.commit
     * @param {Object} obj.dispatch
     *
     * @returns {Promise} - Returns error if the document doesn't exist.
     */
    fetchActiveTheme({ commit, dispatch }) {
      return new Promise((resolve, reject) => {
        const { themes } = tenantCtx()
        const q = query(themes, where('active', '==', true))

        getDocs(q)
          .then(async docSnapshot => {
            const [docRef] = docSnapshot.docs

            if (docRef === undefined) {
              resolve(null)

              return
            }

            // Fetch related resources
            const activeThemeFiles = await dispatch('fetchCurrentFiles', docRef?.id)
            const activeThemeExternalResources = await dispatch('fetchCurrentExternalResources', docRef?.id)

            const activeTheme = {
              data: docRef?.data(),
              files: activeThemeFiles,
              externalResources: activeThemeExternalResources,
            }

            commit('SET_ACTIVE_THEME', activeTheme)

            resolve(activeTheme)
          })
          .catch(error => {
            reject(error)

            store.dispatch('notify', {
              variant: 'danger',
              body: i18n.t('Something went wrong retrieving the {title}', { title: i18n.t('Active Theme') }),
            })
          })
      })
    },

    /**
     * Deactivates every active theme except `exceptId`.
     * Only patches `active` — does not load files (unlike fetchActiveTheme).
     *
     * @param {Object} _ctx
     * @param {string} exceptId - Theme id that may become / stay active
     * @returns {Promise<string[]>} deactivated theme ids
     */
    async deactivateOtherThemes(_ctx, exceptId) {
      const { themes } = tenantCtx()
      const snap = await getDocs(query(themes, where('active', '==', true)))
      const targets = snap.docs.filter(d => d.id !== exceptId)

      await Promise.all(targets.map(async d => {
        await updateDoc(doc(themes, d.id), {
          active: false,
          updatedAt: getUnix(),
        })
        await store.dispatch('revisions/create', {
          event: RevisionEventType.UPDATE_THEMES,
          id: d.id,
          previousValue: { active: true },
          newValue: { active: false },
        })
      }))

      return targets.map(d => d.id)
    },

    /**
     * Fetches document count
     *
     * @param {Object} context - Vuex context
     *
     * @returns {Promise<number>} - Count of matching documents
     */
    fetchCount({ commit }) {
      return new Promise(async (resolve, reject) => {
        try {
          const { themes } = tenantCtx()

          const q = query(
            themes,
            where('deletedAt', '==', null),
          )

          const snapshot = await getCountFromServer(q)
          const { count } = snapshot.data()

          commit('SET_COUNT', count)
          resolve(count)
        } catch (error) {
          store.dispatch('notify', {
            body: i18n.t('Something went wrong retrieving the {title}', { title: i18n.t('themes') }),
            variant: 'danger',
          })
          reject(error)
        }
      })
    },

    /**
     * Fetches a Theme by the given ID.
     *
     * @param {Object} obj
     * @param {string} id The document ID
     *
     * @returns {Promise}
     */
    fetchById({}, id) {
      return new Promise((resolve, reject) => {
        const { themes } = tenantCtx()
        const docRef = doc(themes, id)

        getDoc(docRef)
          .then(docSnapshot => {
            if (!docSnapshot.exists() || docSnapshot.get('deletedAt') !== null) {
              reject(new Error('404'))
            }

            resolve(docSnapshot)
          })
          .catch(error => {
            store.dispatch('notify', {
              variant: 'danger',
              body: i18n.t('Something went wrong retrieving the Theme'),
            })

            reject(error)
          })
      })
    },

    /**
     * Fetches the current Theme by the given ID.
     *
     * @param {Object} obj
     * @param {Object} obj.dispatch
     * @param {Object} obj.commit
     * @param {string} id - The current Theme ID
     *
     * @returns {Promise}
     */
    fetchCurrent({ dispatch, commit }, id) {
      // De-duplicate concurrent requests for the same Theme ID (see comment above).
      if (fetchCurrentInFlight.has(id)) {
        return fetchCurrentInFlight.get(id)
      }

      const promise = new Promise((resolve, reject) => {
        dispatch('fetchById', id)
          .then(async res => {
            // Fetch related resources in parallel instead of sequentially
            const [currentFiles, currentExternalResources] = await Promise.all([
              dispatch('fetchCurrentFiles', id),
              dispatch('fetchCurrentExternalResources', id),
            ])

            commit('SET_CURRENT', res)
            commit('SET_CURRENT_FILES', currentFiles)
            commit('SET_CURRENT_EXTERNAL_RESOURCES', currentExternalResources)
            resolve(res)
          })
          .catch(error => {
            reject(error)
          })
      }).finally(() => fetchCurrentInFlight.delete(id))

      fetchCurrentInFlight.set(id, promise)

      return promise
    },

    /**
     * Fetches the files of the current Theme by the given ID.
     *
     * @param {Object} obj
     * @param {Object} obj.commit
     * @param {string} id - The current Theme ID
     *
     * @returns {Promise}
     */
    fetchCurrentFiles({ commit }, id) {
      return new Promise((resolve, reject) => {
        const { themes } = tenantCtx()
        const filesCollection = collection(themes, id, 'files')

        const q = constructQuery(filesCollection, 'name', {
          where: [where('deletedAt', '==', null)],
        })

        getDocs(q)
          .then(docSnapshot => {
            const files = docSnapshot.docs
              .map(docRef => ({
                id: docRef.id,
                ...docRef.data(),
              }))

            commit('SET_CURRENT_FILES', files)
            resolve(files)
          })
          .catch(error => {
            reject(error)

            store.dispatch('notify', {
              variant: 'danger',
              body: i18n.t('Something went wrong retrieving the {title}', { title: i18n.t('Theme\'s files') }),
            })
          })
      })
    },

    /**
     * Fetches the thumbnails of a Theme by the given ID and target collection and bucket.
     *
     * @param {Object} obj
     * @param {Object} payload.id - The Theme ID
     * @param {Object} payload.target - The target collection and bucket
     *
     * @returns {Promise}
     */
    fetchThemeThumbnails({ }, { id, target = 'specific' }) {
      return new Promise((resolve, reject) => {
        // Get the tenant-specific and shared Themes collection
        const { themes, sharedThemes } = tenantCtx()

        // Get the tenant-specific and client themes root buckets
        const { themesStorage, sharedThemesStorage } = tenantCtx()

        const collections = {
          specific: themes,
          shared: sharedThemes,
        }

        const buckets = {
          specific: themesStorage,
          shared: sharedThemesStorage,
        }

        const filesCollection = collection(collections[target], id, 'files')

        const q = constructQuery(filesCollection, 'name', {
          where: [
            where('deletedAt', '==', null),
            where('type', '==', 'html'),
          ],
        })

        getDocs(q)
          .then(async docSnapshot => {
            const bucket = buckets[target]
            const files = docSnapshot.docs.map(file => file.data())

            // One listAll per path folder instead of getDownloadURL × files × devices
            // (missing PNGs were flooding the network with Storage 404s).
            const pathNames = new Map()
            await Promise.all(
              [...new Set(files.map(f => f.path).filter(Boolean))].map(async path => {
                pathNames.set(path, await listThumbnailNames(bucket, id, path))
              }),
            )

            const thumbnails = await Promise.all(
              files.map(async fileData => {
                const thumbnailUrls = await resolveDeviceThumbnailUrls(
                  bucket,
                  id,
                  fileData,
                  pathNames.get(fileData.path) || new Set(),
                )

                return {
                  name: fileData.name,
                  path: fileData.path,
                  ...thumbnailUrls,
                }
              }),
            )

            resolve(thumbnails)
          })
          .catch(error => {
            console.error(error)

            reject(error)
          })
      })
    },

    /**
     * Fetches the thumbnails of a UI Component by the given Theme ID and target bucket.
     *
     * @param {Object} obj
     * @param {Object} payload.themeId - The Theme ID
     * @param {Object} payload.uiComponent - The UI Component
     * @param {Object} payload.target - The target bucket
     *
     * @returns {Promise}
     */
    fetchUIComponentThumbnails({ }, { themeId, uiComponent, target = 'specific' }) {
      return new Promise(async resolve => {
        const { themesStorage, sharedThemesStorage } = tenantCtx()

        const buckets = {
          specific: themesStorage,
          shared: sharedThemesStorage,
        }

        const urls = await resolveDeviceThumbnailUrls(
          buckets[target],
          themeId,
          uiComponent,
        )

        resolve(urls)
      })
    },
    /**
     * Fetches the current Theme's External Resources.
     *
     * @param {Object} obj
     * @param {Object} obj.commit
     * @param {string} id - The current Theme ID
     *
     * @returns {Promise}
     */
    fetchCurrentExternalResources({ commit }, id) {
      return new Promise((resolve, reject) => {
        const { themes } = tenantCtx()
        const externalResourcesCollection = collection(themes, id, 'externalResources')

        const q = constructQuery(externalResourcesCollection, 'order', {
          where: [where('deletedAt', '==', null)],
        })

        getDocs(q)
          .then(docSnapshot => {
            const externalResources = docSnapshot.docs
              .map(docRef => ({
                id: docRef.id,
                ...docRef.data(),
              }))

            commit('SET_CURRENT_EXTERNAL_RESOURCES', externalResources)
            resolve(externalResources)
          })
          .catch(error => {
            reject(error)

            store.dispatch('notify', {
              variant: 'danger',
              body: i18n.t('Something went wrong retrieving the {title}', {
                title: i18n.t('Theme\'s External Resources'),
              }),
            })
          })
      })
    },

    /**
     * Fetches a file of the current Theme.
     *
     * @param {Object} obj
     * @param {Object} obj.state
     * @param {Object} payload - The payload used to fetch the file
     *
     * @returns {Promise}
     */
    fetchCurrentThemeFile({ state }, payload) {
      return new Promise(async (resolve, reject) => {
        const { themesStorage } = tenantCtx()
        const fileRef = storageRef(
          themesStorage,
          `${state.current.data.id}/${payload.path}/${payload.name}.${payload.type}`,
        )

        getBlob(fileRef)
          .then(fileBlob => {
            resolve(fileBlob)
          })
          .catch(error => {
            store.dispatch('notify', {
              variant: 'danger',
              body: i18n.t('Something went wrong retrieving the {title}', {
                title: i18n.t(`the content of ${payload.name}.${payload.type}`),
              }),
            })

            reject(error)
          })
      })
    },

    /**
     * Fetches a file of the active Theme.
     *
     * @param {Object} obj
     * @param {Object} obj.state
     * @param {Object} payload - The payload used to fetch the file
     *
     * @returns {Promise}
     */
    fetchActiveThemeFile({ state }, payload) {
      return new Promise(async (resolve, reject) => {
        const { themesStorage } = tenantCtx()
        const fileRef = storageRef(
          themesStorage,
          `${state.activeTheme.data.id}/${payload.path}/${payload.name}.${payload.type}`,
        )

        getBlob(fileRef)
          .then(fileBlob => {
            resolve(fileBlob)
          })
          .catch(error => {
            store.dispatch('notify', {
              variant: 'danger',
              body: i18n.t('Something went wrong retrieving the {title}', {
                title: i18n.t(`the content of ${payload.name}.${payload.type}`),
              }),
            })

            reject(error)
          })
      })
    },

    /**
     * Updates the current Theme by the given payload.
     *
     * @param {Object} obj
     * @param {Object} obj.state
     * @param {Object} obj.dispatch
     * @param {Object} payload - The payload used to update the Theme
     *
     * @returns {Promise}
     */
    updateCurrent({ state, dispatch }, payload) {
      return new Promise(async (resolve, reject) => {
        try {
          const previousValue = state.current.data
          const { id } = state.current.snapshot

          const value = {
            ...previousValue,
            ...payload,
            updatedAt: getUnix(),
          }

          // Firestore rejects undefined field values.
          Object.keys(value).forEach(key => {
            if (value[key] === undefined) delete value[key]
          })

          // Must await — previously this resolved before the write finished and
          // also kicked off fetchCurrent (all files), which froze the UI after Save.
          await dispatch('update', {
            value,
            previousValue,
          })

          resolve(id)
        } catch (error) {
          console.debug(error)

          reject(error)
        }
      })
    },

    /**
     * Updates a Theme by the given payload.
     *
     * @param {Object} obj
     * @param {Object} payload.value - The new value
     * @param {Object} payload.previousValue - The previous value
     *
     * @returns {Promise}
     */
    update({}, { value, previousValue }) {
      return new Promise((resolve, reject) => {
        const { themes } = tenantCtx()
        const docRef = doc(themes, value.id)

        updateDoc(docRef, value)
          .then(() => {
            store.dispatch('revisions/create', {
              id: value.id,
              previousValue,
              newValue: { ...value },
              event: RevisionEventType.UPDATE_THEMES,
            })

            resolve(true)
          })
          .catch(error => {
            store.dispatch('notify', {
              body: i18n.t('Something went wrong updating the {title}', { title: i18n.t('Theme') }),
              variant: 'danger',
            })

            reject(error)
          })
      })
    },

    /**
     * Updates the current Theme file by the given payload.
     *
     * @param {Object} obj
     * @param {Object} obj.state
     * @param {Object} obj.getters
     * @param {Object} payload - The payload used to update the file
     *
     * @returns {Promise}
     */
    updateCurrentThemeFile({ state, getters }, payload) {
      return new Promise(async (resolve, reject) => {
        try {
          const { themes, themesStorage } = tenantCtx()
          const docRef = doc(themes, `${state.current.data.id}/files/${payload.id}`)

          // Capture the content-changed signal BEFORE syncing oldValue = newValue.
          // The previous code compared after the sync, so customized never flipped.
          const contentChanged = payload.newValue !== payload.oldValue

          // Let us see if we have to upload something
          if (contentChanged) {
            const file = `${state.current.data.id}/${payload.path}/${payload.name}.${payload.type}`
            const fileRef = storageRef(
              themesStorage,
              file,
            )

            await uploadString(fileRef, payload.newValue)

            // Since we are already on the client side, we can easily take screenshots
            // of UI component elements to update the thumbnails. Doing this server-side is hard and inefficient.
            if (payload.type === 'html') {
              const current = getters.getCurrent
              const currentStyles = getters.getExternalResources('css')
              const currentScripts = getters.getExternalResources('js')
              const externalResources = retrieveExternalResources(current, currentStyles, currentScripts)

              const iframeDoc = updateIframeElement(payload.newValue, state.thumbnailCanvasElement, externalResources, payload.path)
              const iframeParentElement = document.getElementById('thumbnailCanvasContainer')
              const deviceWidths = {
                mobile: 'w-50',
                tablet: 'w-75',
                desktop: 'w-100',
              }

              // Generates a canvas for each device by dynamically mimicking the device width
              const processDevice = async (device, width) => {
                iframeParentElement.classList.add(width) // Set the width class dynamically

                const thumbnailStoragePath = `${state.current.data.id}/${payload.path}/images/${payload.name}.${payload.type}.${device}.png`
                const canvas = await html2canvas(iframeDoc.body)
                const fileThumbnailRef = storageRef(themesStorage, thumbnailStoragePath)
                const thumbnailDataUrl = canvas.toDataURL('image/png', 1)

                iframeParentElement.classList.remove(width)

                await uploadString(fileThumbnailRef, thumbnailDataUrl, 'data_url')
              }

              // Iterate over the deviceWidths and process each device
              const processPromises = Object.entries(deviceWidths).map(([device, width]) => processDevice(device, width))

              await Promise.all(processPromises)
            }

            // Sync the values for the participating units
            // and prevent unnecessary Storage updates
            payload.oldValue = payload.newValue
          }

          // Prevent unnecessary Firestore updates
          delete payload.changed

          const { newValue, oldValue, ...fileData } = payload

          // Hint for the UI; server-side merge computes customization via content hashes.
          const customizedUpdate = (contentChanged && fileData.origin === 'inherited')
            ? { customized: true }
            : {}

          await updateDoc(docRef, {
            ...fileData,
            ...customizedUpdate,
            updatedAt: getUnix(),
          })

          resolve(true)
        } catch (error) {
          console.debug(error)

          store.dispatch('notify', {
            variant: 'danger',
            body: i18n.t('Something went wrong updating the {title}', {
              title: i18n.t(`file ${payload.name}.${payload.type}`),
            }),
          })

          reject(error)
        }
      })
    },

    /**
     * Updates or inserts an External Resource of the current Theme by the given payload.
     *
     * @param {Object} obj
     * @param {Object} obj.state
     * @param {Object} payload - The payload used to update the External Resource
     *
     * @returns {Promise}
     */
    upsertCurrentThemeExternalResource({ state }, payload) {
      return new Promise(async (resolve, reject) => {
        try {
          const { themes } = tenantCtx()
          const externalResources = collection(themes, state.current.data.id, 'externalResources')

          if (!payload.id) {
            const docRef = doc(externalResources)

            setDoc(docRef, { ...payload }, { merge: true })
              .then(() => resolve(docRef))
              .catch(error => reject(error))
          } else {
            const docRef = doc(externalResources, payload.id)

            await updateDoc(docRef, {
              ...payload,
              updatedAt: getUnix(),
            })
          }

          resolve(true)
        } catch (error) {
          console.debug(error)

          store.dispatch('notify', {
            variant: 'danger',
            body: i18n.t('Something went wrong updating the {title}', {
              title: i18n.t(`External Resource ${payload.name}`),
            }),
          })

          reject(error)
        }
      })
    },

    /**
     * Deletes a file of the current Theme by the given payload.
     *
     * @param {Object} obj
     * @param {Object} obj.state
     * @param {Object} payload - The payload used to delete the file
     *
     * @returns {Promise}
     */
    deleteCurrentThemeFile({ state }, payload) {
      return new Promise(async (resolve, reject) => {
        try {
          const { themes } = tenantCtx()
          const { themesStorage } = tenantCtx()
          const docRef = doc(themes, `${state.current.data.id}/files/${payload.id}`)

          const fileRef = storageRef(
            themesStorage,
            `${state.current.data.id}/${payload.path}/${payload.name}.${payload.type}`,
          )

          await deleteObject(fileRef)

          // Soft delete in the DB
          await updateDoc(docRef, {
            deletedAt: getUnix(),
          })

          resolve(true)
        } catch (error) {
          console.debug(error)

          store.dispatch('notify', {
            variant: 'danger',
            body: i18n.t('Something went wrong deleting the {title}', {
              title: i18n.t(`file ${payload.name}.${payload.type}`),
            }),
          })

          reject(error)
        }
      })
    },

    /**
     * Deletes an External Resources of the current Theme by the given payload.
     *
     * @param {Object} obj
     * @param {Object} obj.state
     * @param {Object} payload - The payload used to delete the External Resource
     *
     * @returns {Promise}
     */
    async deleteCurrentThemeExternalResource({ state }, payload) {
      return new Promise((resolve, reject) => {
        try {
          const { themes } = tenantCtx()
          const docRef = doc(themes, `${state.current.data.id}/externalResources/${payload.id}`)

          // Soft delete in the DB
          updateDoc(docRef, {
            deletedAt: getUnix(),
          })
            .then(() => {
              resolve(true)
            })
        } catch (error) {
          console.debug(error)

          store.dispatch('notify', {
            variant: 'danger',
            body: i18n.t('Something went wrong deleting the {title}', {
              title: i18n.t(`External Resource ${payload.name}`),
            }),
          })

          reject(error)
        }
      })
    },

    /**
     * Listens/subscribes to Firestore changes of the current Theme.
     *
     * @param {Object} obj
     * @param {Object} obj.state
     * @param {Object} obj.commit
     *
     * @returns {Object}
     */
    async currentThemeListener({ state, commit }) {
      const { themes } = tenantCtx()
      const docRef = doc(themes, state.current.data.id)

      // Now listen to changes
      const unsubscribe = onSnapshot(docRef, doc => {
        commit('SET_CURRENT', doc)
      })

      return unsubscribe
    },

    /**
     * Soft deletes a Theme by the given payload.
     *
     * @param {Object} obj
     * @param {Object} payload - The payload
     *
     * @returns {Promise}
     */
    softDelete({}, payload) {
      return new Promise((resolve, reject) => {
        const { themes } = tenantCtx()
        const docRef = doc(themes, payload.id)

        updateDoc(docRef, {
          deletedAt: getUnix(),
        })
          .then(() => {
            store.dispatch('revisions/create', {
              event: RevisionEventType.DELETE_THEMES,
              id: payload.id,
              newValue: {},
              previousValue: payload,
            })

            resolve(true)
          })
          .catch(error => {
            reject(error)
          })
      })
    },

    /**
     * Sets the thumbnail canvas element.
     *
     * @param {Object} obj.commit
     * @param {Object} element - The thumbnail canvas element
     *
     * @returns {void}
     */
    setThumbnailCanvasElement({ commit }, element) {
      commit('SET_THUMBNAIL_CANVAS_ELEMENT', element)
    },
  },
}
